import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Mail, ChevronDown, ShoppingCart } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { businessInfo } from '../mock';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const servicesTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update cart count
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const count = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(count);
    };
    
    updateCartCount();
    
    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    
    // Also check on location change
    const interval = setInterval(updateCartCount, 1000);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      clearInterval(interval);
    };
  }, [location]);

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
    }
    setIsServicesOpen(true);
  };

  const handleServicesMouseLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 200);
  };

  // Handle service dropdown click - scrolls to service card
  const handleServiceClick = (serviceSlug) => {
    setIsServicesOpen(false);
    
    // Navigate to services page with hash
    if (location.pathname === '/services') {
      // Already on services page, just scroll to the element
      const element = document.getElementById(`service-${serviceSlug}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-4', 'ring-red-500', 'ring-offset-4');
        setTimeout(() => {
          element.classList.remove('ring-4', 'ring-red-500', 'ring-offset-4');
        }, 2000);
      }
    } else {
      // Navigate to services page with hash
      navigate(`/services#${serviceSlug}`);
    }
  };

  return (
    <>
      {/* Top bar with contact info */}
      <div className="bg-white border-b border-gray-200 py-2 hidden md:block">
        <div className="container mx-auto px-4 flex justify-end items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <Phone className="w-4 h-4" />
            <a href={`tel:${businessInfo.phone}`} className="hover:text-red-600 transition-colors">
              {businessInfo.phone}
            </a>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <Mail className="w-4 h-4" />
            <a href={`mailto:${businessInfo.email}`} className="hover:text-red-600 transition-colors">
              {businessInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://lirp.cdn-website.com/1d57ed00/dms3rep/multi/opt/Streetsville+Eyecare+Logo+Full-1920w.png"
                alt="Streetsville Eyecare Logo"
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop menu */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/" 
                className={`font-medium transition-colors ${
                  location.pathname === '/' ? 'text-red-600' : 'text-gray-700 hover:text-red-600'
                }`}
              >
                HOME
              </Link>
              <Link 
                to="/about" 
                className={`font-medium transition-colors ${
                  location.pathname === '/about' ? 'text-red-600' : 'text-gray-700 hover:text-red-600'
                }`}
              >
                ABOUT
              </Link>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
              >
                <button className={`font-medium transition-colors flex items-center gap-1 py-2 ${
                  location.pathname === '/services' ? 'text-red-600' : 'text-gray-700 hover:text-red-600'
                }`}>
                  SERVICES
                  <ChevronDown className="w-4 h-4" />
                </button>
                {isServicesOpen && (
                  <div className="absolute top-full left-0 pt-2 w-64">
                    <div className="bg-white shadow-lg rounded-md py-2 border border-gray-200">
                      <Link to="/services" className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors" onClick={() => setIsServicesOpen(false)}>
                        All Services
                      </Link>
                      <button onClick={() => handleServiceClick('on-site-eye-exams')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        On-Site Eye Exams
                      </button>
                      <button onClick={() => handleServiceClick('custom-fit-eyeglasses')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        Custom Fit Eyeglasses
                      </button>
                      <button onClick={() => handleServiceClick('custom-fit-sunglasses')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        Custom Fit Sunglasses
                      </button>
                      <button onClick={() => handleServiceClick('on-site-glasses-repairs')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        On-Site Glasses Repairs
                      </button>
                      <button onClick={() => handleServiceClick('contact-lenses-sold-on-site')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        Contact Lenses
                      </button>
                      <button onClick={() => handleServiceClick('special-needs-optician')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors">
                        Special Needs Optician
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Link 
                to="/collection" 
                className={`font-medium transition-colors ${
                  location.pathname === '/collection' ? 'text-red-600' : 'text-gray-700 hover:text-red-600'
                }`}
              >
                SHOP
              </Link>

              <Link 
                to="/contact" 
                className={`font-medium transition-colors ${
                  location.pathname === '/contact' ? 'text-red-600' : 'text-gray-700 hover:text-red-600'
                }`}
              >
                CONTACT
              </Link>
              
              {/* Cart Icon */}
              <Link 
                to="/cart" 
                className="relative text-gray-700 hover:text-red-600 transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              <Link to="/" className="text-left text-gray-700 hover:text-red-600 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>HOME</Link>
              <Link to="/about" className="text-left text-gray-700 hover:text-red-600 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>ABOUT</Link>
              <Link to="/services" className="text-left text-gray-700 hover:text-red-600 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>SERVICES</Link>
              <Link to="/collection" className="text-left text-gray-700 hover:text-red-600 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>SHOP</Link>
              <Link to="/contact" className="text-left text-gray-700 hover:text-red-600 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>CONTACT</Link>
              <Link to="/cart" className="text-left text-gray-700 hover:text-red-600 font-medium py-2 flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                <ShoppingCart className="w-5 h-5" />
                CART {cartCount > 0 && <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">{cartCount}</span>}
              </Link>
              <Link to="/contact" className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors text-center" onClick={() => setIsMobileMenuOpen(false)}>
                Book Appointment
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
